<script lang="ts" setup>
import { vLazyVideo } from '@/composables/useLazyVideo'
const { trackEvent } = useCnzz()

const { t } = useI18n()

definePageMeta({
  layout: 'page',
  pageTransition: false,
  layoutTransition: false,
})
useHead({
  title: t('biopharmaceutical.pageTitle'),
  meta: [
    {
      name: 'description',
      content: t('seo.biopharmaceutical.description'),
    },
    {
      name: 'keywords',
      content: t('seo.biopharmaceutical.keywords'),
    },
    {
      property: 'og:title',
      content: t('seo.biopharmaceutical.title'),
    },
    {
      property: 'og:description',
      content: t('seo.biopharmaceutical.description'),
    },
    {
      property: 'og:type',
      content: 'website',
    },
    {
      property: 'og:image',
      content: 'https://www.qboson.com/favicon.ico',
    },
    {
      property: 'og:locale',
      content: 'zh_CN',
    },
    {
      name: 'twitter:card',
      content: 'summary_large_image',
    },
    {
      name: 'twitter:title',
      content: t('seo.biopharmaceutical.title'),
    },
    {
      name: 'twitter:description',
      content: t('seo.biopharmaceutical.description'),
    },
    {
      name: 'twitter:image',
      content: 'https://www.qboson.com/favicon.ico',
    },
    {
      name: 'author',
      content: '玻色量子',
    },
  ],
})

const cards = [
  {
    key: 'molecular',
    // icon: 'https://images-cloud.qboson.com/website/fenzi.mp4',
    icon: 'https://images-cloud.qboson.com/website/biopharmaceutical/fenzi_new.png',
  },
  {
    key: 'protein',
    // icon: 'https://images-cloud.qboson.com/website/danbai.mp4',
    icon: 'https://images-cloud.qboson.com/website/biopharmaceutical/danbai_new.png',
  },
  {
    key: 'SimulationPlatform',
    // icon: 'https://images-cloud.qboson.com/website/minipingtai.mp4',
    icon: 'https://images-cloud.qboson.com/website/biopharmaceutical/minipingtai.gif',
  },
  {
    key: 'PerformanceComparison',
    icon: 'https://images-cloud.qboson.com/website/biopharmaceutical/xingneng_new.png',
  },
]

// 四个量子模块数据，包含轮播项数量
const quantumSections = [
  {
    key: 'virus',
    // video: 'https://images-cloud.qboson.com/website/shengwu.webm',
    video:
      'https://images-cloud.qboson.com/website/biopharmaceutical/shengwubingud%20.png',
    isVideo: false,
    bgColor: '#fff',
    imageLeft: true,
    carouselCount: 5,
  },
  {
    key: 'lifeSystem',
    video:
      'https://images-cloud.qboson.com/website/biopharmaceutical/nengliang_new.png',
    isVideo: false,
    bgColor: '#F7F9FD',
    imageLeft: false,
    carouselCount: 5,
  },
  {
    key: 'drugDev',
    // video: 'https://images-cloud.qboson.com/website/yaowu.webm',
    video:
      'https://images-cloud.qboson.com/website/biopharmaceutical/yaowu_new.png',
    isVideo: false,
    bgColor: '#fff',
    imageLeft: true,
    carouselCount: 5,
  },
  {
    key: 'material',
    // video: 'https://images-cloud.qboson.com/website/cailiao.webm',
    video:
      'https://images-cloud.qboson.com/website/biopharmaceutical/cailiao2.gif',
    isVideo: false,
    bgColor: '#F7F9FD',
    imageLeft: false,
    carouselCount: 4,
  },
]

// 每个模块的轮播索引
const carouselIndexMap = ref<Record<string, number>>({
  virus: 0,
  lifeSystem: 0,
  drugDev: 0,
  material: 0,
})

// Refs for measuring elements
const sectionRefs = ref<
  Record<
    string,
    {
      container: HTMLElement | null
      wrapper: HTMLElement | null
      items: HTMLElement[]
    }
  >
>({})

// Metrics for calculations
const sectionMetrics = reactive<
  Record<
    string,
    {
      containerWidth: number
      totalWidth: number
      maxOffset: number
      itemWidths: number[]
    }
  >
>({})

const setContainerRef = (el: any, key: string) => {
  if (!sectionRefs.value[key])
    sectionRefs.value[key] = { container: null, wrapper: null, items: [] }
  sectionRefs.value[key].container = el as HTMLElement
}

const setWrapperRef = (el: any, key: string) => {
  if (!sectionRefs.value[key])
    sectionRefs.value[key] = { container: null, wrapper: null, items: [] }
  sectionRefs.value[key].wrapper = el as HTMLElement
}

const setItemRef = (el: any, key: string, index: number) => {
  if (!sectionRefs.value[key])
    sectionRefs.value[key] = { container: null, wrapper: null, items: [] }
  // index is 1-based from v-for
  if (el) sectionRefs.value[key].items[index - 1] = el as HTMLElement
}

const updateMetrics = () => {
  for (const key in sectionRefs.value) {
    const refs = sectionRefs.value[key]
    if (!refs.container || !refs.wrapper) continue

    const containerWidth = refs.container.clientWidth
    const style = window.getComputedStyle(refs.container)
    const paddingLeft = parseFloat(style.paddingLeft) || 0
    const paddingRight = parseFloat(style.paddingRight) || 0
    const contentWidth = containerWidth - paddingLeft - paddingRight

    const items = refs.items
    const itemWidths = items.map((el) => (el ? el.offsetWidth : 0))

    // Use scrollWidth for total width to be more accurate with gaps and layout
    const totalWidth = refs.wrapper.scrollWidth

    const maxOffset = Math.max(0, totalWidth - contentWidth)

    sectionMetrics[key] = {
      containerWidth,
      totalWidth,
      maxOffset,
      itemWidths,
    }
  }
}

// Initialize metrics
let resizeObserver: ResizeObserver | null = null

onMounted(() => {
  // Give a small delay for layout to stabilize
  setTimeout(() => updateMetrics(), 100)

  resizeObserver = new ResizeObserver(() => {
    updateMetrics()
  })

  if (document.body) resizeObserver.observe(document.body)
  window.addEventListener('resize', updateMetrics)
})

onBeforeUnmount(() => {
  if (resizeObserver) resizeObserver.disconnect()
  window.removeEventListener('resize', updateMetrics)
})

// 是否可以向前
function canPrev(key: string) {
  return carouselIndexMap.value[key] > 0
}

// 是否可以向后
function canNext(key: string, itemCount: number) {
  const metrics = sectionMetrics[key]
  if (!metrics) return carouselIndexMap.value[key] < itemCount - 1

  const currentTranslate = getTranslateX(key, itemCount)
  // If we are effectively at the end (within 1px tolerance), we can't go next
  return currentTranslate < metrics.maxOffset - 1
}

// 计算偏移量
function getTranslateX(key: string, itemCount: number) {
  const metrics = sectionMetrics[key]
  if (!metrics) return 0

  const index = carouselIndexMap.value[key]
  const gap = 30

  let currentOffset = 0
  for (let i = 0; i < index; i++) {
    const w = metrics.itemWidths[i] || 0
    currentOffset += w + gap
  }

  // Snap to end if we are very close (within 10px) or past it
  if (currentOffset >= metrics.maxOffset - 10) {
    return metrics.maxOffset
  }

  return Math.min(currentOffset, metrics.maxOffset)
}

function prevSlide(key: string) {
  if (canPrev(key)) {
    carouselIndexMap.value[key]--
  }
}

function nextSlide(key: string, itemCount: number) {
  if (canNext(key, itemCount)) {
    carouselIndexMap.value[key]++
  }
}

// 鼠标滚轮左右滚动 - 只有卡片数量 >= 5 时才允许滚动
function onWheel(e: WheelEvent, key: string, itemCount: number) {
  if (itemCount < 5) return // 卡片数量小于5时不允许滚动
  e.preventDefault()
  if (e.deltaY > 0 || e.deltaX > 0) {
    nextSlide(key, itemCount)
  } else if (e.deltaY < 0 || e.deltaX < 0) {
    prevSlide(key)
  }
}

// 在线咨询弹窗逻辑
const showConsultDialog = ref(false)

// 打开弹窗
const openDialog = () => {
  showConsultDialog.value = true
  trackEvent('生物制药', '点击', '前往咨询按钮')
}

// 卡片悬停状态管理
const hoveredCardIndex = ref<number | null>(2)

// 前往咨询 - 友盟上报
const trackEventService = () => {
  trackEvent('生物制药', '点击', '前往咨询按钮')
}

// 立即体验 - 友盟上报
const trackEventExperience = () => {
  trackEvent('生物制药', '点击', '立即体验按钮')
}
</script>

<template>
  <div class="w-full bg-[#F7F9FD]">
    <!-- Hero 区域 -->
    <section class="relative overflow-hidden">
      <!-- 背景图片 - web端 -->
      <div class="hidden md:block w-full">
        <video
          v-lazy-video
          data-src="https://images-cloud.qboson.com/website/shengwu_new.mp4"
          class="w-full"
          style="aspect-ratio: 2940 / 1260"
          loop
          muted
          playsinline
          disablepictureinpicture
        ></video>
      </div>
      <!-- 背景图片 - 移动端 -->
      <div class="md:hidden w-full">
        <img
          class="w-full h-auto"
          src="https://images-cloud.qboson.com/website/shengwu_mobile.png"
          alt=""
        />
      </div>

      <!-- 内容 -->
      <div
        class="absolute inset-0 z-10 md:max-w-[1200px] mx-auto h-full flex flex-col px-[16px] md:px-0 mt-[70px] md:mt-[140px]"
      >
        <p
          class="text-[24px] md:text-[45px] font-medium mb-[10px] md:mb-[25px]"
        >
          {{ $t('biopharmaceutical.hero.title') }}
        </p>
        <p
          class="text-xs md:text-xl font-normal md:max-w-[548px] md:leading-[34px]"
        >
          {{ $t('biopharmaceutical.hero.description') }}
        </p>
        <!-- 前往咨询按钮 -->
        <NuxtLink
          to="/product/productConsultation"
          class="md:hidden bg-[#FFFFFF99] rounded-[12px] w-[104px] text-[14px] font-medium text-center py-[8px] mt-[20px] mb-[200px] join-border text-[#333] cursor-pointer inline-block"
          @click="trackEventService"
        >
          {{ $t('biopharmaceutical.hero.btnConsult') }}
        </NuxtLink>
        <div
          class="hidden md:block bg-transparent w-[144px] text-[16px] font-medium text-center py-[12px] mt-[62px] mb-[200px] join-border text-[#333] cursor-pointer"
          @click="openDialog"
        >
          {{ $t('biopharmaceutical.hero.btnConsult') }}
        </div>
      </div>
    </section>

    <!-- 天工量子模拟平台 -->
    <section class="py-[40px] md:py-[30px]">
      <div class="md:max-w-[1200px] mx-auto px-6">
        <!-- 标题 -->
        <div class="flex flex-col items-center mb-[24px] md:mb-12">
          <p
            class="text-[16px] md:text-[37px] font-medium md:font-normal text-[#000] mb-[14px] md:mb-[15px]"
          >
            {{ $t('biopharmaceutical.platform.title') }}
          </p>
          <p
            class="text-sm md:tex-xl text-[#333] mx-auto text-center md:text-left"
          >
            {{ $t('biopharmaceutical.platform.description') }}
          </p>
          <!-- 前往咨询按钮 -->
          <a
            href="https://qdock.qboson.com/#/condition"
            target="_blank"
            class="w-[104px] md:w-[144px] text-[14px] md:text-[16px] font-medium text-center py-[6px] mt-[22px] md:mt-[25px] join-border text-[#333] cursor-pointer"
            @click="trackEventExperience"
          >
            {{ $t('biopharmaceutical.platform.btnExperience') }}
          </a>
        </div>

        <!-- web端 -->
        <div
          class="hidden md:grid h-[504px] grid-cols-4 gap-6 bg-white rounded-[20px]"
          style="box-shadow: 0 6px 30px 27px rgba(219, 232, 230, 0.35)"
        >
          <!-- 卡片遍历 -->
          <div
            v-for="(card, index) in cards"
            :key="card.key"
            class="flex flex-col justify-center p-6 text-center"
            @mouseenter="hoveredCardIndex = index"
            @mouseleave="hoveredCardIndex = null"
          >
            <div
              class="w-[143px] h-[143px] mx-auto mb-4 bg-white rounded-full flex items-center justify-center overflow-hidden transition-transform duration-300 ease-in-out"
              :class="{
                'scale-[1.8] z-10':
                  hoveredCardIndex === null
                    ? index === 2
                    : hoveredCardIndex === index,
              }"
              style="box-shadow: 0 4px 25px 0 rgba(0, 0, 0, 0.2)"
            >
              <!-- <video
                v-lazy-video
                class="w-full h-full object-cover"
                :data-src="card.icon"
                loop
                muted
                playsinline
                disablepictureinpicture
              /> -->
              <img class="w-full h-full object-cover" :src="card.icon" alt="" />
            </div>
            <p class="text-2xl md:text-2xl font-medium text-[#333] mt-[110px]">
              {{ $t(`biopharmaceutical.cards.${card.key}.title`) }}
            </p>
            <p class="text-lg md:text-lg font-normal text-[#777]">
              {{ $t(`biopharmaceutical.cards.${card.key}.subtitle`) }}
            </p>
          </div>
        </div>

        <!-- 移动端 - 使用懒加载视频 -->
        <div
          class="md:hidden w-full flex gap-[10px] overflow-x-auto scrollbar-hide"
        >
          <!-- 卡片遍历 -->
          <div
            v-for="card in cards"
            :key="card.key"
            class="w-[250px] flex-shrink-0 flex flex-col bg-white rounded-[12px] border border-[#EEEEEE] justify-center py-6 text-center"
          >
            <div
              class="w-[130px] h-[130px] mx-auto mb-[16px] bg-white rounded-full flex items-center justify-center overflow-hidden shadow-[4px_4px_12px_0px_rgba(87,126,211,0.08)]"
            >
              <video
                v-lazy-video
                class="w-full h-full object-cover"
                :data-src="card.icon"
                loop
                muted
                playsinline
                disablepictureinpicture
              />
            </div>
            <div class="h-[1px] bg-[#EEE]"></div>
            <p class="text-[14px] font-medium text-[#000] mt-[16px] mb-[10px]">
              {{ $t(`biopharmaceutical.cards.${card.key}.title`) }}
            </p>
            <p class="text-xs font-normal text-[#777]">
              {{ $t(`biopharmaceutical.cards.${card.key}.subtitle`) }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- 量子模块遍历 -->
    <section
      v-for="(section, sectionIndex) in quantumSections"
      :key="section.key"
      class="py-[40px] md:py-24"
      :class="sectionIndex % 2 === 0 ? 'bg-white' : 'bg-[#F7F9FD]'"
    >
      <div class="max-w-[1200px] mx-auto px-6">
        <p
          class="text-[16px] md:text-[32px] font-medium md:font-bold text-[#000] md:text-[#333] text-center mb-[28px] md:mb-12"
        >
          {{ $t(`biopharmaceutical.sections.${section.key}.title`) }}
        </p>

        <div
          class="flex flex-col md:flex-row justify-center items-center gap-8 md:gap-16"
          :class="{ 'md:flex-row-reverse': !section.imageLeft }"
        >
          <!-- 视频 - 使用懒加载 -->
          <div
            class="w-full md:w-1/2"
            :style="{ backgroundColor: section.bgColor }"
          >
            <video
              v-if="section.isVideo"
              v-lazy-video
              class="w-full h-full object-cover"
              :data-src="section.video"
              loop
              muted
              playsinline
              disablepictureinpicture
              :style="{ backgroundColor: section.bgColor }"
            />
            <img v-else :src="section.video" alt="" />
          </div>

          <!-- 文字内容 -->
          <div
            class="bg-white w-full md:w-[calc(50%-100px)] rounded-[12px] p-[15px] md:py-[45px] md:px-[50px] border-[1px] border-[#eee] md:border-none shadow-[4px_4px_12px_0px_rgba(87,126,211,0.08)] md:shadow-[0_7px_30px_8px_#DAE0ED]"
          >
            <p
              v-if="
                $t(`biopharmaceutical.sections.${section.key}.subtitle`) !== ''
              "
              class="mb-[12px] md:mb-[27px] text-[14px] md:text-[20px] text-[#000] md:text-[#193757] font-medium"
            >
              {{ $t(`biopharmaceutical.sections.${section.key}.subtitle`) }}
            </p>

            <!-- web端 -->
            <div
              v-for="itemIndex in 3"
              :key="itemIndex"
              class="hidden md:flex items-center text-sm md:text-base text-[#666] leading-relaxed"
              :class="{ 'mb-4': itemIndex < 3 }"
            >
              <div
                class="w-[11px] h-[11px] rounded-[50%] mr-[20px] flex-shrink-0"
                style="
                  background: linear-gradient(
                    124deg,
                    #36ba93 -2.73%,
                    #37a9c5 50.34%,
                    #073c89 103.64%
                  );
                "
              ></div>
              <p>
                {{
                  $t(
                    `biopharmaceutical.sections.${section.key}.items[${itemIndex - 1}]`,
                  )
                }}
              </p>
            </div>

            <!-- 移动端 -->
            <div
              v-for="itemIndex in 3"
              :key="itemIndex"
              class="md:hidden flex items-center text-sm md:text-base text-[#666] leading-relaxed"
              :class="{ 'mb-[5px]': itemIndex < 3 }"
            >
              <div
                class="w-[5px] h-[5px] rounded-[1px] mr-[20px] flex-shrink-0 bg-[#349CC7]"
              ></div>
              <p class="text-[12px] text-[#000] font-normal leading-[18px]">
                {{
                  $t(
                    `biopharmaceutical.sections.${section.key}.items[${itemIndex - 1}]`,
                  )
                }}
              </p>
            </div>
          </div>
        </div>

        <!-- 轮播 - 只在 carouselCount >= 5 时显示箭头 -->
        <div class="hidden relative md:flex items-center mt-12">
          <!-- 左箭头 -->
          <button
            v-if="section.carouselCount >= 5"
            class="w-[40px] h-[40px] flex items-center justify-center text-[#999] hover:text-[#333] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            :disabled="!canPrev(section.key)"
            @click="prevSlide(section.key)"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          <!-- 轮播内容 -->
          <div
            :ref="(el) => setContainerRef(el, section.key)"
            class="h-[150px] flex items-center justify-center flex-1 overflow-hidden px-[20px] mx-4"
            @wheel="(e) => onWheel(e, section.key, section.carouselCount)"
          >
            <div
              :ref="(el) => setWrapperRef(el, section.key)"
              class="flex gap-[30px] transition-transform duration-300 ease-in-out"
              :style="{
                transform: `translateX(-${getTranslateX(section.key, section.carouselCount)}px)`,
              }"
            >
              <div
                v-for="index in section.carouselCount"
                :key="index"
                :ref="(el) => setItemRef(el, section.key, index)"
                class="flex-shrink-0 bg-white rounded-[12px] px-[10px] py-[20px] flex items-center gap-[10px] shadow-[0_7px_30px_8px_#DAE0ED]"
                style="box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.08)"
              >
                <!-- 六边形图标 -->
                <div
                  class="carousel-icon flex-shrink-0 w-[30px] h-[30px] flex items-center justify-center"
                >
                  <img
                    src="@/assets/images/biopharmaceutical/Frame.png"
                    alt=""
                  />
                </div>
                <!-- 文字 -->
                <div class="flex-1 min-w-0">
                  <p class="text-[16px] font-normal text-[#000] leading-[26px]">
                    {{
                      $t(
                        `biopharmaceutical.sections.${section.key}.carouselItems[${index - 1}].title`,
                      )
                    }}
                  </p>
                  <p class="text-[16px] font-normal text-[#000] leading-[26px]">
                    {{
                      $t(
                        `biopharmaceutical.sections.${section.key}.carouselItems[${index - 1}].subtitle`,
                      )
                    }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- 右箭头 -->
          <button
            v-if="section.carouselCount >= 5"
            class="w-[40px] h-[40px] flex items-center justify-center text-[#999] hover:text-[#333] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            :disabled="!canNext(section.key, section.carouselCount)"
            @click="nextSlide(section.key, section.carouselCount)"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>

        <!-- 移动端轮播 -->
        <div class="relative md:hidden flex items-center">
          <!-- 轮播内容 -->
          <div
            class="w-full h-[110px] flex items-center flex-1 overflow-x-auto scrollbar-hide"
          >
            <div class="flex gap-[10px]">
              <div
                v-for="index in section.carouselCount"
                :key="index"
                class="flex-shrink-0 bg-white rounded-[12px] px-[10px] py-[20px] border border-[#eee] flex items-center gap-[8px] shadow-[4px_4px_12px_rgba(87, 126, 211, 0.08)]"
                style="box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.08)"
              >
                <!-- 六边形图标 -->
                <div
                  class="carousel-icon flex-shrink-0 w-[35px] h-[35px] flex items-center justify-center"
                >
                  <img
                    src="@/assets/images/biopharmaceutical/Frame.png"
                    alt=""
                  />
                </div>
                <!-- 文字 -->
                <div class="flex-1 min-w-0">
                  <p class="text-[12px] font-normal text-[#000] leading-[18px]">
                    {{
                      $t(
                        `biopharmaceutical.sections.${section.key}.carouselItems[${index - 1}].title`,
                      )
                    }}
                  </p>
                  <p class="text-[12px] font-normal text-[#000] leading-[18px]">
                    {{
                      $t(
                        `biopharmaceutical.sections.${section.key}.carouselItems[${index - 1}].subtitle`,
                      )
                    }}
                  </p>
                </div>
              </div>
              <!-- 右侧占位元素 -->
              <div class="hidden md:block w-[16px] flex-shrink-0"></div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 业务咨询及商务合作弹窗 -->
    <ConsultationDialog v-model:show="showConsultDialog" />
  </div>
</template>

<style scoped>
video {
  display: block;
  object-fit: cover;
}

.join-border {
  position: relative;
  z-index: 1;
}

.join-border::before {
  content: '';
  position: absolute;
  inset: 0;
  padding: 1px;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    rgba(55, 187, 148, 1),
    rgba(1, 46, 128, 1)
  );
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  z-index: -1;
}

.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>
